import { useEffect, useState } from 'react';

type Props = {
  endsAt: string | null;
  nowServerMs: () => number;
  /** Append this label after the countdown (e.g., "left"). */
  suffix?: string;
  /** Class applied to the wrapper span. */
  className?: string;
};

/**
 * Renders "3d 12h 4m 18s left" style countdown, ticking every second.
 *
 * Uses the server-synced clock (via nowServerMs) so it matches what
 * the server will actually do when close time arrives.
 */
export function Countdown({ endsAt, nowServerMs, suffix, className }: Props) {
  const [label, setLabel] = useState(() => computeLabel(endsAt, nowServerMs()));

  useEffect(() => {
    if (!endsAt) return;
    const id = setInterval(() => {
      setLabel(computeLabel(endsAt, nowServerMs()));
    }, 1000);
    return () => clearInterval(id);
  }, [endsAt, nowServerMs]);

  if (!endsAt) {
    return <span className={className}>—</span>;
  }

  return (
    <span className={className}>
      {label}
      {suffix ? ` ${suffix}` : ''}
    </span>
  );
}

function computeLabel(endsAt: string | null, nowMs: number): string {
  if (!endsAt) return '—';
  const endMs = new Date(endsAt).getTime();
  const diffMs = endMs - nowMs;
  if (diffMs <= 0) return 'ended';

  const seconds = Math.floor(diffMs / 1000);
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
